import { Check, Star } from "lucide-react";
import { PRICING } from "@/constants/testIds";

const quarterlyFeatures = [
  "4 scheduled visits per year",
  "Interior + exterior treatment",
  "Free re-treatment between visits",
  "Seasonal pest rotation strategy",
  "Priority phone line + bilingual support",
  "30-day satisfaction guarantee",
];

const oneTimeFeatures = [
  "Single intensive treatment",
  "Full property inspection report",
  "Heaviest concentration formulas",
  "Same-week scheduling",
  "Written 30-day warranty",
  "Best for severe infestations",
];

const Pricing = () => {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section id="pricing" className="border-b border-zinc-900 bg-[#F4F4F5]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="text-center mb-16">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#D9480F] font-bold mb-4">
            [ 03 ] — Service Plans
          </p>
          <h2 className="font-display uppercase font-semibold leading-[0.9] tracking-tighter text-4xl md:text-6xl">
            Two plans. <br />
            <span className="text-[#0B2B40]">Pick your protection level.</span>
          </h2>
          <p className="mt-6 text-zinc-700 max-w-2xl mx-auto">
            Most homes thrive on quarterly maintenance. Got a serious problem right now?
            Our one-time premium treatment hits hard and fast.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 max-w-5xl mx-auto">
          {/* Quarterly - Featured */}
          <div className="relative bg-[#0B2B40] text-white border-2 border-zinc-900 hard-shadow-orange">
            <div className="absolute -top-4 left-8 bg-[#F59E0B] text-black border-2 border-black px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.2em] font-bold">
              ★ Recommended
            </div>
            <div className="p-8 md:p-10">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#F59E0B] mb-2">
                    Plan A
                  </div>
                  <h3 className="font-display uppercase text-4xl md:text-5xl font-semibold tracking-tight">
                    Quarterly
                  </h3>
                </div>
                <div className="text-right">
                  <div className="font-display text-5xl text-[#F59E0B]">$$</div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-300">
                    Standard Rate
                  </div>
                </div>
              </div>
              <p className="text-zinc-200 leading-relaxed mb-8 border-l-2 border-[#F59E0B] pl-4">
                Constant protection. Four scheduled visits hit pests through every season —
                spring nests, summer mosquitoes, fall rodents, winter perimeter.
              </p>
              <ul className="space-y-3 mb-10">
                {quarterlyFeatures.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm md:text-base">
                    <Check className="w-5 h-5 text-[#F59E0B] mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <button
                data-testid={PRICING.quarterlyCta}
                onClick={() => scrollTo("contact")}
                className="w-full bg-[#D9480F] text-white px-6 py-4 font-display uppercase tracking-tight text-base hover:bg-[#E8590C] transition-colors border-2 border-black"
              >
                Start Quarterly Plan →
              </button>
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400 text-center mt-4">
                Call for property-specific quote
              </p>
            </div>
          </div>

          {/* One-Time - Premium */}
          <div className="bg-white border-2 border-zinc-900 hard-shadow">
            <div className="p-8 md:p-10">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#D9480F] mb-2">
                    Plan B · Premium
                  </div>
                  <h3 className="font-display uppercase text-4xl md:text-5xl font-semibold tracking-tight text-zinc-900">
                    One-Time
                  </h3>
                </div>
                <div className="text-right">
                  <div className="font-display text-5xl text-[#D9480F]">$$$</div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                    Premium Rate
                  </div>
                </div>
              </div>
              <p className="text-zinc-700 leading-relaxed mb-8 border-l-2 border-[#D9480F] pl-4">
                Aggressive single-treatment knockdown. For when you've got an active infestation
                and need it gone — yesterday. Heaviest formulations we offer.
              </p>
              <ul className="space-y-3 mb-10">
                {oneTimeFeatures.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm md:text-base text-zinc-800">
                    <Check className="w-5 h-5 text-[#D9480F] mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <button
                data-testid={PRICING.oneTimeCta}
                onClick={() => scrollTo("contact")}
                className="w-full bg-[#0B2B40] text-white px-6 py-4 font-display uppercase tracking-tight text-base hover:bg-[#143b58] transition-colors border-2 border-black"
              >
                Book One-Time Treatment →
              </button>
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500 text-center mt-4">
                Same-week scheduling available
              </p>
            </div>
          </div>
        </div>

        <div className="mt-12 flex items-center justify-center gap-3 text-sm text-zinc-600">
          <Star className="w-4 h-4 text-[#F59E0B] fill-[#F59E0B]" />
          <span className="font-mono text-xs uppercase tracking-[0.2em]">
            All plans · Licensed · Insured · Bilingual
          </span>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
